import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BackendError, bootstrapDevice, checkActivation, getAppConfig, LicenseStatus, verifyLicense } from '../api/backendAPI';
import { temporaryBootstrapFailure } from '../utils/bootstrapPolicy';
import { normalizePlaylistActivationUrl, resolvePlaylistActivationUrl } from '../utils/activationUrl';
import { formatActivationDisplayId, getDeviceIdentity } from '../utils/deviceIdentity';

const STORAGE_KEY = 'ott_device_v1';
const BOOTSTRAP_RETRY_INTERVAL_MS = 8_000;
const PLAYLIST_ACTIVATION_URL_STORAGE_KEY = 'ott_playlist_activation_url_v1';
const DEFAULT_VERIFICATION_INTERVAL_MS = 300_000;
const DEFAULT_GRACE_PERIOD_MS = 3_600_000;
const DEFAULT_RETRY_DELAYS_MS = [15_000, 30_000, 60_000, 120_000, 300_000];
const DEFAULT_MAX_RETRY_INTERVAL_MS = 300_000;
const DEFAULT_JITTER_RATIO = 0.2;

function applyJitter(baseMs: number, jitterRatio: number): number {
  const amplitude = Math.floor(baseMs * jitterRatio);
  if (amplitude <= 0) return Math.max(1_000, baseMs);
  return Math.max(1_000, baseMs + Math.floor((Math.random() * 2 - 1) * amplitude));
}

interface PersistedDevice {
  deviceId: string;
  activationDisplayId: string;
  activationPassword: string;
  licenseStatus: LicenseStatus;
}

export type LicenseVerificationPhase = 'healthy' | 'blocked' | 'recovered';

interface DeviceContextType {
  deviceId: string;
  activationDisplayId: string;
  activationPassword: string;
  licenseStatus: LicenseStatus;
  isDemo: boolean;
  hardwareUid: string;
  displayCode: string;
  devicePassword: string;
  playlistActivationUrl: string;
  licenseVerificationPhase: LicenseVerificationPhase;
  acknowledgeLicenseRecovery: () => void;
  refreshActivation: () => Promise<void>;
}

const DeviceContext = createContext<DeviceContextType | null>(null);

function loadPersisted(): PersistedDevice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as PersistedDevice : null;
  } catch { return null; }
}

function persist(data: PersistedDevice): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch { /* unavailable */ }
}

function generateDeviceId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = (Math.random() * 16) | 0;
    return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
}

export const DeviceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<PersistedDevice>({
    deviceId: '', activationDisplayId: '', activationPassword: '', licenseStatus: 'bootstrapping',
  });
  const [hardwareUid, setHardwareUid] = useState('');
  const [licenseVerificationPhase, setLicenseVerificationPhaseState] = useState<LicenseVerificationPhase>('healthy');
  const [playlistActivationUrl, setPlaylistActivationUrl] = useState(() => {
    let cached: string | null = null;
    try { cached = localStorage.getItem(PLAYLIST_ACTIVATION_URL_STORAGE_KEY); } catch { /* ignore */ }
    return resolvePlaylistActivationUrl(undefined, cached);
  });

  const intervalMsRef = useRef(DEFAULT_VERIFICATION_INTERVAL_MS);
  const graceMsRef = useRef(DEFAULT_GRACE_PERIOD_MS);
  const retryDelaysMsRef = useRef<number[]>(DEFAULT_RETRY_DELAYS_MS);
  const maxRetryMsRef = useRef(DEFAULT_MAX_RETRY_INTERVAL_MS);
  const jitterRatioRef = useRef(DEFAULT_JITTER_RATIO);
  const timerRef = useRef<number | null>(null);
  const bootstrapRetryRef = useRef<number | null>(null);
  const pendingDeviceIdRef = useRef('');
  const failureCountRef = useRef(0);
  const lastSuccessAtRef = useRef(0);
  const verifyOnceRef = useRef<(() => Promise<void>) | null>(null);
  const phaseRef = useRef<LicenseVerificationPhase>('healthy');

  const setPhase = useCallback((phase: LicenseVerificationPhase) => {
    phaseRef.current = phase;
    setLicenseVerificationPhaseState(phase);
  }, []);

  const clearVerificationTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleVerification = useCallback((delayMs: number) => {
    clearVerificationTimer();
    if (document.visibilityState === 'hidden') return;
    timerRef.current = window.setTimeout(
      () => void verifyOnceRef.current?.(),
      applyJitter(delayMs, jitterRatioRef.current),
    );
  }, [clearVerificationTimer]);

  const acceptStatus = useCallback((licenseStatus: LicenseStatus) => {
    setState((previous) => {
      const updated = { ...previous, licenseStatus };
      persist(updated);
      return updated;
    });
  }, []);

  const verifyOnce = useCallback(async () => {
    const saved = loadPersisted();
    if (!saved?.deviceId || saved.licenseStatus !== 'active') {
      clearVerificationTimer();
      return;
    }
    try {
      const result = await verifyLicense(saved.deviceId, saved.activationPassword);
      failureCountRef.current = 0;
      lastSuccessAtRef.current = Date.now();
      acceptStatus(result.licenseStatus);
      if (result.licenseStatus !== 'active' || result.playbackAllowed === false) {
        setPhase('healthy');
        clearVerificationTimer();
        return;
      }
      if (phaseRef.current === 'blocked') setPhase('recovered');
      scheduleVerification(intervalMsRef.current);
    } catch {
      failureCountRef.current += 1;
      if (!lastSuccessAtRef.current) lastSuccessAtRef.current = Date.now();
      if (Date.now() - lastSuccessAtRef.current >= graceMsRef.current && phaseRef.current === 'healthy') {
        setPhase('blocked');
      }
      const retryDelay = retryDelaysMsRef.current[failureCountRef.current - 1] ?? maxRetryMsRef.current;
      scheduleVerification(Math.min(retryDelay, maxRetryMsRef.current));
    }
  }, [acceptStatus, clearVerificationTimer, scheduleVerification, setPhase]);
  verifyOnceRef.current = verifyOnce;

  const startVerification = useCallback((authoritativeSuccess: boolean, immediate = false) => {
    failureCountRef.current = 0;
    if (authoritativeSuccess || !lastSuccessAtRef.current) lastSuccessAtRef.current = Date.now();
    scheduleVerification(immediate ? 1_000 : intervalMsRef.current);
  }, [scheduleVerification]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const config = await getAppConfig();
        let cachedUrl: string | null = null;
        try { cachedUrl = localStorage.getItem(PLAYLIST_ACTIVATION_URL_STORAGE_KEY); } catch { /* ignore */ }
        const currentUrl = normalizePlaylistActivationUrl(config.playlistActivationUrl);
        if (!cancelled) setPlaylistActivationUrl(resolvePlaylistActivationUrl(currentUrl, cachedUrl));
        if (currentUrl) {
          try { localStorage.setItem(PLAYLIST_ACTIVATION_URL_STORAGE_KEY, currentUrl); } catch { /* ignore */ }
        }
        const settings = config.licenseVerificationSettings;
        if (settings) {
          if (Number.isFinite(settings.intervalMs)) intervalMsRef.current = Math.max(60_000, Math.min(900_000, settings.intervalMs!));
          if (Number.isFinite(settings.gracePeriodMs)) graceMsRef.current = Math.max(intervalMsRef.current, settings.gracePeriodMs!);
          if (Array.isArray(settings.retryDelaysMs) && settings.retryDelaysMs.length)
            retryDelaysMsRef.current = settings.retryDelaysMs.map((value) => Math.max(1_000, value));
          if (Number.isFinite(settings.maxRetryIntervalMs)) maxRetryMsRef.current = Math.max(60_000, Math.min(900_000, settings.maxRetryIntervalMs!));
          if (Number.isFinite(settings.jitterRatio)) jitterRatioRef.current = Math.max(0, Math.min(0.5, settings.jitterRatio!));
        }
      } catch { /* compiled defaults */ }

      const identity = await getDeviceIdentity();
      if (cancelled) return;
      const uid = identity.hardwareUid;
      if (uid) setHardwareUid(uid);
      const saved = loadPersisted();
      const activationDisplayId = saved?.activationDisplayId || '';
      const activationPassword = saved?.activationPassword || '';
      const evidence = {
        platformDeviceId: identity.platformDeviceId,
        platformDeviceIdType: identity.platformDeviceIdType,
        platform: identity.platform,
      };

      if (saved?.deviceId) {
        setState(saved);
        try {
          const result = await bootstrapDevice({
            deviceId: saved.deviceId, activationDisplayId, activationPassword, ...evidence,
          });
          if (cancelled) return;
          const synced: PersistedDevice = {
            deviceId: result.device.id || saved.deviceId,
            activationDisplayId: result.device.activationDisplayId || activationDisplayId,
            activationPassword: result.device.activationPassword,
            licenseStatus: result.licenseStatus,
          };
          setState(synced);
          persist(synced);
          if (synced.licenseStatus === 'active') startVerification(true, true);
          else clearVerificationTimer();
        } catch (error) {
          if (cancelled) return;
          const status = error instanceof BackendError ? error.status : 0;
          if (temporaryBootstrapFailure(status) && saved.licenseStatus === 'active') {
            startVerification(false, true);
          } else if (temporaryBootstrapFailure(status)) {
            try {
              const check = await checkActivation(saved.deviceId);
              if (!cancelled) acceptStatus(check.licenseStatus);
            } catch { /* polling retries later */ }
          } else {
            clearVerificationTimer();
            if (status === 403) acceptStatus('revoked');
            else setState({ ...saved, licenseStatus: 'bootstrapping' });
          }
        }
        return;
      }

      const deviceId = pendingDeviceIdRef.current || (pendingDeviceIdRef.current = generateDeviceId());
      try {
        const result = await bootstrapDevice({ deviceId, ...evidence });
        if (cancelled) return;
        const fresh: PersistedDevice = {
          deviceId: result.device.id || deviceId,
          activationDisplayId: result.device.activationDisplayId || activationDisplayId,
          activationPassword: result.device.activationPassword,
          licenseStatus: result.licenseStatus,
        };
        setState(fresh);
        persist(fresh);
        if (fresh.licenseStatus === 'active') startVerification(true, true);
      } catch {
        if (cancelled) return;
        setState({ deviceId, activationDisplayId, activationPassword, licenseStatus: 'bootstrapping' });
        bootstrapRetryRef.current = window.setTimeout(() => void init(), BOOTSTRAP_RETRY_INTERVAL_MS);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') clearVerificationTimer();
      else if (loadPersisted()?.licenseStatus === 'active') scheduleVerification(1_000);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    void init();
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearVerificationTimer();
      if (bootstrapRetryRef.current !== null) window.clearTimeout(bootstrapRetryRef.current);
    };
  }, [acceptStatus, clearVerificationTimer, scheduleVerification, startVerification]);

  const refreshActivation = useCallback(async () => {
    if (!state.deviceId) return;
    try {
      const check = await checkActivation(state.deviceId);
      acceptStatus(check.licenseStatus);
      if (check.licenseStatus === 'active') startVerification(true, true);
      else clearVerificationTimer();
    } catch { /* activation screen keeps polling */ }
  }, [acceptStatus, clearVerificationTimer, startVerification, state.deviceId]);

  const acknowledgeLicenseRecovery = useCallback(() => {
    if (phaseRef.current === 'recovered') setPhase('healthy');
  }, [setPhase]);
  const isDemo = ['expired', 'revoked', 'pending_activation'].indexOf(state.licenseStatus) >= 0;

  return <DeviceContext.Provider value={{
    deviceId: state.deviceId,
    activationDisplayId: state.activationDisplayId,
    activationPassword: state.activationPassword,
    licenseStatus: state.licenseStatus,
    isDemo,
    hardwareUid,
    displayCode: state.activationDisplayId ? formatActivationDisplayId(state.activationDisplayId) : '',
    devicePassword: state.activationPassword,
    playlistActivationUrl,
    licenseVerificationPhase,
    acknowledgeLicenseRecovery,
    refreshActivation,
  }}>{children}</DeviceContext.Provider>;
};

export function useDevice(): DeviceContextType {
  const context = useContext(DeviceContext);
  if (!context) throw new Error('useDevice must be used inside <DeviceProvider>');
  return context;
}
